/** @format */

import { useState, useEffect, useRef } from "react";
import { usePrevious } from "react-use";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectJoinRequestsOfCircle } from "../../../redux/selectors/circleSelector";
import {
	selectCurrentUser,
	selectIsError,
	selectUserMap,
} from "../../../redux/selectors/userSelector";
import { AppDispatch } from "../../../redux/store/store";
import UserProfileHeader from "../../userprofile-header/UserProfileHeader";
import {
	Avatar,
	Box,
	Card,
	Container,
	Grid,
	Stack,
	Table,
	TableBody,
	TableContainer,
	TablePagination,
	Typography,
} from "@mui/material";
import DrawerNavbar from "../../drawerNavBar/DrawerNavbar";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import Scrollbar from "../../scrollbar/Scrollbar";
import CircleTableHead from "../../circle-contribution-table/CircleTableHead";
import JoinRequestTableRow from "./JoinRequestTableRow";
import { fetchJoinCircleRequestsOfCircleByCircleLeaderId } from "../../../redux/actions/circleAction";
import { getMultipleUsersById } from "../../../redux/actions/userActions";
import NotificationModal from "../notification-modal/NotificationModal";

interface JoinRequestsOfCircleProps {
	open: boolean;
	handleClose: () => void;
	userData: any;
}

const JoinRequestsOfCircle = (props: JoinRequestsOfCircleProps) => {
	const dispatch = useDispatch<AppDispatch>();

	const userData = useSelector(selectCurrentUser);

	const userMap = useSelector(selectUserMap) || {};

	const circleJoinRequestsInfo = useSelector(selectJoinRequestsOfCircle) || [];

	const error = useSelector(selectIsError);

	const navigate = useNavigate();

	const [initialLoading, setInitialLoading] = useState(true);

	const [page, setPage] = useState(0);

	const [rowsPerPage, setRowsPerPage] = useState(5);

	const [openNav, setOpenNav] = useState(false);

	const [showNotificationModal, setShowNotificationModal] = useState(false);

	const [notifyCircle, setNotifyCircle] = useState({ name: "", image: "" });

	const [timeOutId, setTimeOutId] = useState<any>(null);

	const [notificationModalContent, setNotificationModalContent] = useState("");

	const [notificationTitle, setNotificationTitle] = useState("");

	const [refresh, setRefresh] = useState(false);

	const ROW_HEIGHT = 75;
	const HEADER_HEIGHT = 50;
	const MIN_TABLE_HEIGHT = 300;

	/*
  we need to fetch all the circle that the current logged-in user is unionleader or leader of that circle, and then render each join request a each circle has receive
  1. we need to identify all the circles that the logged in user is the leader of
  2. obtain those circleId
  3. make a call and get those join request for each circle
  4. render those join request data on the table
   */

	useEffect(() => {
		let isMounted = true;
		const fetchData = async () => {
			try {
				setInitialLoading(true);
				if (userData?._id) {
					const result = await dispatch(
						fetchJoinCircleRequestsOfCircleByCircleLeaderId(userData?._id)
					);
					if (isMounted && result) {
						setInitialLoading(false);
					}
				}
			} catch (error: any) {
				if (isMounted) {
					setInitialLoading(false);
				}
			} finally {
				setInitialLoading(false);
			}
		};

		fetchData();

		return () => {
			isMounted = false;
		};
	}, [dispatch, userData?._id, refresh]);

	const circles = (circleJoinRequestsInfo as { circles: any[] })?.circles || [];

	const combinedRequests = circles.flatMap((circle: any) =>
		circle.joinRequests.map((req: any) => ({
			circleId: circle._id,
			circleName: circle.circleName,
			circleImage: circle.circleImage,
			description: circle.description,
			joinRequestId: req._id,
			reason: req.reason,
			rating: req.rating,
			status: req.status,
			deniedAt: req.deniedAt,
			created_At: req.created_At,
			updated_At: req.updated_At,
			userId: req.userId._id || req.userId,
		}))
	);

	// const uniqueCombinedJoinRequests = combinedRequests.filter(
	//   (request, index, self) =>
	//     index === self.findIndex((r) => r.circleId === request.circleId)
	// );

	// collect all userId's for a single fetch
	const allUserIds = Array.from(
		new Set(combinedRequests.map((req: any) => req.userId))
	);

	// fetch all user details needed
	// useEffect(() => {
	//   if (allUserIds.length > 0) {
	//     if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
	//     debounceTimeout.current = setTimeout(() => {
	//       dispatch(getMultipleUsersById(allUserIds));
	//     }, 300);
	//   }
	//   return () => {
	//     if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
	//   };
	// }, [dispatch, allUserIds]);

	const prevUserIds = usePrevious(allUserIds);

	useEffect(() => {
		if (
			allUserIds.length > 0 &&
			JSON.stringify(allUserIds) !== JSON.stringify(prevUserIds)
		) {
			dispatch(getMultipleUsersById(allUserIds));
		}
	}, [dispatch, allUserIds, prevUserIds]);

	const mergedRequests = combinedRequests.map((item: any) => {
		const user = userMap[item.userId] || {};
		return {
			...item,
			firstName: user.firstName || "",
			lastName: user.lastName || "",
			email: user.email || "",
			profileImage: user.profileImage || "",
		};
	});

	const initialHeight = Math.min(combinedRequests.length * ROW_HEIGHT, 500);

	const [tableHeight, setTableHeight] = useState(initialHeight);

	// Calculate actual rows in the current page
	const actualRowsCurrentPage = Math.min(
		combinedRequests.length - page * rowsPerPage,
		rowsPerPage
	);

	// Calculate the new height
	const calculateTableHeight = () => {
		const rowsHeight = actualRowsCurrentPage * ROW_HEIGHT;
		const totalHeight = rowsHeight + HEADER_HEIGHT; // include header height
		return Math.max(totalHeight, MIN_TABLE_HEIGHT); // ensure it's not smaller than MIN_TABLE_HEIGHT
	};

	// Updates table height when page, rowsPerPage, or combinedData changes
	useEffect(() => {
		const newHeight = calculateTableHeight();
		setTableHeight(newHeight);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, rowsPerPage, combinedRequests]);

	const handleChangePage = (event: any, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: any) => {
		setPage(0);
		setRowsPerPage(parseInt(event.target.value, 10));
	};

	const sortedRequests = [...mergedRequests].sort((a: any, b: any) => {
		const circleCompare = a.circleName.localeCompare(b.circleName);

		if (circleCompare !== 0) return circleCompare;

		return a.lastName.localeCompare(b.lastName);
	});

	const paginatedRequests = sortedRequests.slice(
		page * rowsPerPage,
		page * rowsPerPage + rowsPerPage
	);

	const handleRefresh = () => {
		setRefresh((prev) => !prev);
	};

	const [acceptRequest, setAcceptRequest] = useState<{
		success: boolean;
		message: string;
		circleImage: string;
	} | null>(null);

	const [denyRequest, setDenyRequest] = useState<{
		success: boolean;
		message: string;
		circleImage: string;
	} | null>(null);

	useEffect(() => {
		if (acceptRequest && acceptRequest.success) {
			setNotificationModalContent(acceptRequest.message);
			setNotifyCircle((prevState) => ({
				...prevState,
				image: acceptRequest.circleImage,
			}));
			setShowNotificationModal(true);

			const newTimeOutId = setTimeout(() => {
				setShowNotificationModal(false);
				navigate("/join-requests");
			}, 5000);

			setTimeOutId(newTimeOutId);
		}
	}, [acceptRequest, navigate]);

	const handleAcceptJoinREequest = (
		success: boolean,
		message: string,
		circleImage: string
	) => {
		setAcceptRequest({ success, message, circleImage });
		handleRefresh();
	};

	useEffect(() => {
		if (denyRequest && denyRequest.success) {
			setNotificationModalContent(denyRequest.message);
			setNotifyCircle((prevState) => ({
				...prevState,
				image: denyRequest.circleImage,
			}));
			setShowNotificationModal(true);

			const newTimeOutId = setTimeout(() => {
				setShowNotificationModal(false);
				navigate("/join-requests");
			}, 5000);

			setTimeOutId(newTimeOutId);
		}
	}, [denyRequest, navigate]);

	const handleDenyJoinRequest = (
		success: boolean,
		message: string,
		circleImage: string
	) => {
		setDenyRequest({ success, message, circleImage });
		handleRefresh();
	};

	// const handleDenyNotification = (circleName: string, circleImage: string) => {
	//   const message = `You declined to join ${circleName}. No further action is needed.`;
	//   setNotifyCircle((prevState) => ({
	//     ...prevState,
	//     image: circleImage,
	//   }));
	//   handleShowNotificationModal("Request Denied", message);
	// };

	const handleShowNotificationModal = (title: string, message: string) => {
		setNotificationTitle(title);
		setNotificationModalContent(message);
		setShowNotificationModal(true);
	};

	const handleCloseNotificationModal = () => {
		setShowNotificationModal(false);
		setNotificationTitle("");
		setNotificationModalContent("");
		navigate(`/user/${userData?._id}`);
	};

	return (
		<>
			<UserProfileHeader onOpenNav={() => setOpenNav(true)} />

			<Box
				sx={{
					minHeight: 1,
					display: "flex",
					flexDirection: { xs: "column", lg: "row" },
				}}>
				<DrawerNavbar openNav={openNav} onCloseNav={() => setOpenNav(false)} />

				{initialLoading ? (
					<LoadingSpinner
						loading={initialLoading}
						size={70}
						sx={{
							position: "absolute",
						}}
					/>
				) : (
					<Container
						sx={{
							display: "flex",
							justifyContent: "center",
							alignContent: "center",
							alignItems: "center",
							// height: 00,
							maxHeight: "100%",
							mt: 12,
						}}>
						{error || mergedRequests.length === 0 ? (
							<Stack
								direction="row"
								alignItems="center"
								justifyContent="center"
								textAlign="center"
								sx={{ mt: 25 }}>
								<Typography
									variant="h4"
									sx={{
										fontFamily: "'Montserrat', sans-serif",
										fontSize: "1rem",
										fontWeight: 999,
										textAlign: "center",
									}}>
									{error || "You Have Not Received Any Join Request So Far."}
								</Typography>
							</Stack>
						) : (
							<Card sx={{ width: "100%" }}>
								<Stack
									// direction="row"
									alignItems="center"
									justifyContent="space-between"
									mt={3}
									mb={3}>
									<Typography
										variant="h4"
										sx={{
											fontFamily: "'Montserrat', sans-serif",
											fontSize: "1rem",
											fontWeight: 999,
										}}>
										{sortedRequests?.length > 0
											? "Pending join requests for circles you lead"
											: "Pending join request for circle you lead"}
									</Typography>
								</Stack>
								<Scrollbar sx={{ height: tableHeight }}>
									<TableContainer sx={{ overflow: "auto" }}>
										<Table sx={{ minWidth: 999 }}>
											<CircleTableHead
												headLabel={[
													{ id: "circle", label: "Circle" },
													{ id: "Request By", label: "Request By" },
													{ id: "More Info", label: "More Info" },
													{ id: "accept", label: "Accept" },
													{ id: "deny", label: "Deny" },
												]}
											/>
											<TableBody>
												{paginatedRequests.map((row: any, i: any) => {
													return (
														<JoinRequestTableRow
															key={`${row.circleId}-${row.userId}-${i}`}
															requestId={row.joinRequestId}
															circleImage={row?.circleImage}
															circleName={row.circleName}
															circleId={row.circleId}
															profileImage={row.profileImage}
															firstName={row.firstName}
															lastName={row.lastName}
															// email={row.email}
															reason={row.reason}
															rating={row.rating}
															selected={undefined}
															isVerified={undefined}
															status={row.status ?? ""}
															onAcceptJoinRequest={(success, message) =>
																handleAcceptJoinREequest(
																	success,
																	message,
																	row.circleImage
																)
															}
															onDenyJoinRequest={(success, message) => {
																handleDenyJoinRequest(
																	success,
																	message,
																	row.circleImage
																);
															}}
															onSuccess={() =>
																handleShowNotificationModal(
																	"Request Accepted",
																	`Congratulations! You have added a new member to ${row.circleName}.`
																)
															}
															onError={() =>
																handleShowNotificationModal(
																	"Request Denied",
																	"An unexpected error occurred. Please try again."
																)
															}
														/>
													);
												})}
											</TableBody>
										</Table>
									</TableContainer>
								</Scrollbar>
								<TablePagination
									page={page}
									component="div"
									count={sortedRequests.length}
									rowsPerPage={rowsPerPage}
									onPageChange={handleChangePage}
									rowsPerPageOptions={[5, 10, 25]}
									onRowsPerPageChange={handleChangeRowsPerPage}
								/>
							</Card>
						)}
					</Container>
				)}
			</Box>

			{showNotificationModal && (
				<NotificationModal
					open={showNotificationModal}
					title={notificationTitle}
					handleClose={handleCloseNotificationModal}
					sx={{
						width: "auto",
						maxWidth: "100%",
						margin: "0",
					}}>
					<Grid
						container
						spacing={2}
						direction="row"
						alignItems="center"
						justifyContent="center"
						sx={{
							margin: "0",
							width: "100%",
							padding: "0",
						}}>
						<Grid item sx={{ maxWidth: "100%", flexBasis: "auto" }}>
							<Avatar
								src={notifyCircle.image}
								sx={{ width: 60, height: 60 }}
								alt="Congrats Icon"
							/>
						</Grid>
						<Grid item xs={9}>
							<Typography
								variant="body2"
								sx={{
									fontSize: 20,
									color: "text.secondary",
									overflow: "hidden",
									overflowWrap: "break-word",
									maxWidth: "100%",
								}}>
								{notificationModalContent}
							</Typography>
						</Grid>
					</Grid>
				</NotificationModal>
			)}
		</>
	);
};

export default JoinRequestsOfCircle;
